import { useMemo } from 'react';
import { useIntl, type IntlFormatters } from 'react-intl';
import { z } from 'zod';
import {
  createStudentTravelCollectionMembersBodyItem,
  createSchoolTransportationCollectionMembersBodyItem,
  createChildAndYouthCollectionMembersBodyItem,
  createStudentTravelCollectionMembersBodyNationalIdentityNumberRegExp as ninRegex,
  updateCollectionMemberBody,
} from '@/api/trafikportalen';
import { blankToUndefined } from '@/helpers/trimData';
import { normalizeCareOf } from '@/helpers/careOf';
import { streetSchema } from '@/helpers/address';

export type StudentFormType = 'transportation' | 'travels' | 'child-and-youth';
export type StudentFormMode = 'create' | 'update';

const travellerFieldsSchema = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  email: z.string().email().optional(),
  national_identity_number: z.string().regex(ninRegex),
  delivery_address: z
    .object({
      street: z.string().min(1),
      zip_code: z.string().min(1),
      city: z.string().min(1),
      care_of: z.string().min(1).optional(),
    })
    .optional(),
});

const personalFieldOverrides = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  national_identity_number: z.string().regex(ninRegex),
  email: z.string().email().optional(),
});

const optionalEmailKeys = [
  'email',
  'guardian1_email',
  'guardian2_email',
  'bobcat_private_email',
] as const;

const addressKeys = [
  'delivery_address',
  'guardian1_address',
  'guardian2_address',
  'school_address',
] as const;

const normalizeOptionalEmails = (obj: Record<string, unknown>) => {
  for (const key of optionalEmailKeys) {
    const v = obj[key];
    if (typeof v !== 'string') continue;
    obj[key] = blankToUndefined(v.trim());
  }
};

const normalizeAddressCareOf = (obj: Record<string, unknown>) => {
  for (const key of addressKeys) {
    const address = obj[key];
    if (typeof address !== 'object' || address === null) continue;
    const { care_of: careOf, ...rest } = address as Record<string, unknown>;
    const name = normalizeCareOf(careOf);
    obj[key] = name ? { ...rest, care_of: name } : rest;
  }
};

const preprocessFormData = (data: unknown) => {
  if (typeof data === 'object' && data !== null) {
    const obj = { ...(data as Record<string, unknown>) };
    normalizeOptionalEmails(obj);
    normalizeAddressCareOf(obj);

    if (typeof obj.phone === 'string') {
      obj.phone = blankToUndefined(obj.phone.trim());
    }

    if (
      typeof obj.national_identity_number === 'string' &&
      !obj.national_identity_number.includes('-')
    ) {
      const nin = obj.national_identity_number;
      if (nin.length === 10) {
        const yearPart = parseInt(nin.slice(0, 2), 10);
        const cutoff = new Date().getFullYear() % 100;
        const century = yearPart > cutoff ? '19' : '20';
        obj.national_identity_number =
          century + nin.slice(0, 6) + '-' + nin.slice(6);
      } else if (nin.length === 12) {
        obj.national_identity_number = nin.slice(0, 8) + '-' + nin.slice(8);
      }
    }

    return obj;
  }
  return data;
};

const updateCollectionMemberWithTravellerBody =
  updateCollectionMemberBody.merge(travellerFieldsSchema);

const baseSchemas: Record<
  StudentFormMode,
  Record<StudentFormType, z.ZodTypeAny>
> = {
  create: {
    transportation: createSchoolTransportationCollectionMembersBodyItem.and(
      personalFieldOverrides,
    ),
    travels: createStudentTravelCollectionMembersBodyItem.and(
      personalFieldOverrides,
    ),
    'child-and-youth': createChildAndYouthCollectionMembersBodyItem.and(
      personalFieldOverrides,
    ),
  },
  update: {
    transportation: updateCollectionMemberWithTravellerBody,
    travels: updateCollectionMemberWithTravellerBody,
    'child-and-youth': updateCollectionMemberWithTravellerBody,
  },
};

const createStreetRules = (formatMessage: IntlFormatters['formatMessage']) => {
  const address = (hasCareOfField: boolean) =>
    z
      .object({ street: streetSchema(formatMessage, { hasCareOfField }) })
      .optional();

  return z.object({
    delivery_address: address(true),
    guardian1_address: address(true),
    guardian2_address: address(true),
    school_address: address(false),
  });
};

// Define that this is a zod schema
type SchemaMapType = {
  [K in StudentFormMode]: {
    [T in StudentFormType]: z.ZodType;
  };
};

// Type helper, use this when you nned to get the type based on mode and type
export type SchemaType<
  M extends StudentFormMode,
  T extends StudentFormType,
> = z.infer<SchemaMapType[M][T]>;

export const useStudentFormSchema = (
  mode: StudentFormMode,
  type: StudentFormType,
) => {
  const { formatMessage } = useIntl();

  return useMemo(
    () =>
      z.preprocess(
        preprocessFormData,
        baseSchemas[mode][type].and(createStreetRules(formatMessage)),
      ),
    [mode, type, formatMessage],
  );
};
